import { Transaction } from '../services/transaction.service';
import { AdminTenantKey } from '../admin/models/admin.models';
import { DateUtils } from './date-utils';
import { getWrappedByKeyName } from '../admin/tenants/key-management/models/key-table.models';

export interface CsvColumn {
  field: string;
  header: string;
}

// Drive the export from the columns currently shown in the table (visibleColumns)
// so the CSV always matches what the user sees — including the category-gated
// "Originated From" / "User" columns.
export function convertTransactionsToCSV(
  transactions: Transaction[],
  columns: CsvColumn[],
): string {
  const csvRows = [
    columns.map(col => escapeCsvValue(col.header)).join(','),
    ...transactions.map(transaction =>
      columns
        .map(col => escapeCsvValue((transaction as unknown as Record<string, unknown>)[col.field]))
        .join(','),
    ),
  ];

  return csvRows.join('\n');
}

export function convertKeysToCSV(keys: AdminTenantKey[]): string {
  const headers = [
    'Type',
    'Name',
    'Version',
    'Algorithm',
    'Region',
    'KCV',
    'KSI',
    'Is Default',
    'Wrapped Key Block',
    'Wrapped By',
    'Key Alias',
    'Wrapped By Key Name',
    'Wrapped By Key ARN',
    'Created At'
  ];

  const csvRows = [
    headers.join(','),
    ...keys.map(key => [
      escapeCsvValue(key.type),
      escapeCsvValue(key.name),
      escapeCsvValue(key.version),
      escapeCsvValue(key.algorithm),
      escapeCsvValue(key.region),
      escapeCsvValue(key.kcv),
      escapeCsvValue(key.ksi),
      escapeCsvValue(key.isDefault),
      escapeCsvValue(key.wrappedKeyBlock),
      escapeCsvValue(key.wrappedBy),
      escapeCsvValue(key.keyAlias),
      escapeCsvValue(getWrappedByKeyName(key) || ''),
      escapeCsvValue(key.wrappedByKeyArn),
      escapeCsvValue(key.createdAt ? DateUtils.formatDateTime(key.createdAt) : '')
    ].join(','))
  ];

  return csvRows.join('\n');
}

export function escapeCsvValue(value: any): string {
  if (value === null || value === undefined) {
    return 'NA';
  }
  let stringValue = String(value);
  
  if (stringValue.length === 0) {
    return 'NA';
  }
  
  if (stringValue.length > 0) {
    const firstChar = stringValue[0];
    if (firstChar === '=' || firstChar === '+' || firstChar === '-' || 
        firstChar === '@' || firstChar === '\t' || firstChar === '\r') {
      stringValue = "'" + stringValue;
    }
  }
  
  const needsQuoting = stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n');

  if (needsQuoting) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

export function downloadCSV(csvContent: string, fileName: string): void {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
